import { MaterialBillingAttention, useMaterialReview } from "@/components/MaterialBillingAttention";
import { TravelBillingPreview } from "@/components/TravelBillingPreview";
import { SupplierPricingInline } from "@/components/invoices/SupplierPricing";
// Charge-Up Unbilled Workspace + Invoice Review — /app/jobs/:id/charge-up
// Same source data as the job (timesheets, material_movements) via
// GET /jobs/{id}/charge-up/unbilled — this is purely a review/selection
// UI over that data. Creating the invoice hands off to the EXISTING
// Customer Invoice system (POST /jobs/{id}/charge-up/invoice → InvoiceWorkspace).
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { SupplierDeliveries } from "@/components/invoices/SupplierItems";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  ArrowLeft, Hammer, Truck, Boxes, AlertTriangle, Loader2, ChevronDown, Receipt,
} from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${(n || 0).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const GROUPS = [
  { key: "labour", label: "Labour", icon: Hammer },
  { key: "plant", label: "Plant", icon: Truck },
  { key: "materials", label: "Materials & Supplies", icon: Boxes },
];
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function ChargeUpWorkspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const location = useLocation();
  const materialReview = useMaterialReview(location.state?.materialReviewToken);
  const [searchParams] = useSearchParams();
  const [needsReview, setNeedsReview] = useState([]);
  const [draftMaterialCount, setDraftMaterialCount] = useState(0);
  const [job, setJob] = useState(null);
  const [entries, setEntries] = useState([]);
  const [labourPreview, setLabourPreview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState(todayISO());
  const [statusFilter, setStatusFilter] = useState(() => ["all", "in_draft", "billed"].includes(searchParams.get("status")) ? searchParams.get("status") : "unbilled");
  const [selected, setSelected] = useState(() => new Set());
  const [overrides, setOverrides] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [rateDialog, setRateDialog] = useState(null); // entry being fixed
  const [rateValue, setRateValue] = useState("");
  const [creating, setCreating] = useState(false);
  const [draftInvoices, setDraftInvoices] = useState([]);
  const [draftChoice, setDraftChoice] = useState(false);
  const [orgDefaultRate, setOrgDefaultRate] = useState(undefined);
  const [defaultRateInput, setDefaultRateInput] = useState("");
  const [savingDefaultRate, setSavingDefaultRate] = useState(false);

  useEffect(() => {
    let ok = true;
    api.get(`/org/me`).then(({ data }) => { if (ok) setOrgDefaultRate(data?.default_labour_charge_out_rate ?? null); })
      .catch(() => { if (ok) setOrgDefaultRate(null); });
    return () => { ok = false; };
  }, []);

  const canEditRates = ["admin", "office", "super_admin"].includes(user?.role);
  const missingLabourCount = entries.filter((e) => e.source_type === "labour" && e.billing_state === "unbilled" && e.missing_rate).length;
  const saveDefaultRate = async () => {
    const val = Number(defaultRateInput);
    if (!Number.isFinite(val) || val <= 0) { toast.error("Enter a charge-out rate greater than $0/hr"); return; }
    setSavingDefaultRate(true);
    try {
      await api.patch(`/org/labour-charge-out-rate`, { default_labour_charge_out_rate: val });
      setOrgDefaultRate(val);
      setDefaultRateInput("");
      toast.success(`Default charge-out rate set — $${val}/hr for all workers`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save the default rate");
    } finally {
      setSavingDefaultRate(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: j }, { data: u }] = await Promise.all([
        api.get(`/jobs/${id}`),
        api.get(`/jobs/${id}/charge-up/unbilled`, { params: { from_date: fromDate || undefined, to_date: toDate || undefined, status: statusFilter } }),
      ]);
      setJob(j);
      setLabourPreview(u.labour_preview || []);
      setNeedsReview(u.needs_review || []);
      setDraftMaterialCount(u.draft_material_count || 0);
      const loaded = (u.entries || []).map((e) => {
        const rate = overrides[e.lock_key];
        return e.billing_state === "unbilled" && e.job_material_id && rate != null && Number(rate) > 0
          ? { ...e, rate: Number(rate), missing_rate: false } : e;
      });
      setEntries(loaded);
      setSelected(() => {
        const next = new Set();
        loaded.forEach((e) => { if (e.billing_state === "unbilled" && !e.missing_rate && !e.requires_selection && !e.evidence_changed) next.add(e.lock_key); });
        return next;
      });
    } catch {
      toast.error("Couldn't load Charge-Up data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, statusFilter]);

  // Existing draft invoices for this job — so a missed item can be added to one
  // instead of accidentally creating a duplicate invoice.
  useEffect(() => {
    let ok = true;
    api.get(`/customer-invoices`, { params: { job_id: id }, __flSkipReadCache: true })
      .then(({ data }) => { if (ok) setDraftInvoices((Array.isArray(data) ? data : []).filter(inv => inv.status === "draft")); })
      .catch(() => { if (ok) setDraftInvoices([]); });
    return () => { ok = false; };
  }, [id, creating]);

  const refreshPricing = async () => {
    const { data } = await api.get(`/jobs/${id}/charge-up/unbilled`, { params: { from_date: fromDate || undefined, to_date: toDate || undefined, status: statusFilter } });
    setEntries(data.entries || []);
    setLabourPreview(data.labour_preview || []);
    setSelected(previous => new Set([...previous].filter(key => (data.entries || []).some(e => e.lock_key === key && !e.missing_rate && e.billing_state === "unbilled"))));
  };

  const grouped = useMemo(() => {
    const g = { labour: [], plant: [], materials: [] };
    entries.forEach((e) => g[e.source_type]?.push(e));
    return g;
  }, [entries]);

  const toggle = (key) => setSelected((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const selectAll = (group) => setSelected((prev) => {
    const n = new Set(prev);
    grouped[group].forEach((e) => { if (e.billing_state === "unbilled" && !e.missing_rate && !e.evidence_changed) n.add(e.lock_key); });
    return n;
  });
  const deselectAll = (group) => setSelected((prev) => {
    const n = new Set(prev);
    grouped[group].forEach((e) => n.delete(e.lock_key));
    return n;
  });

  const effRate = (e) => (!e.supplier_refs && e.billing_state === "unbilled" && overrides[e.lock_key] != null ? Number(overrides[e.lock_key]) : e.rate);
  const effAmount = (e) => e.supplier_refs || (e.source_type === "labour" && e.billing_state !== "unbilled") ? e.amount : Math.round((e.qty || 0) * (effRate(e) || 0) * 100) / 100;

  const selectedTotal = entries.filter((e) => selected.has(e.lock_key)).reduce((s, e) => s + effAmount(e), 0);
  const subtotal = selectedTotal;
  const gst = subtotal * 0.15;
  const grandTotal = subtotal + gst;

  const openSetRate = (e) => { setRateDialog(e); setRateValue(e.rate != null ? String(e.rate) : ""); };
  const saveRate = async () => {
    if (!rateDialog || rateValue === "") return;
    const val = Number(rateValue);
    if (!Number.isFinite(val) || val <= 0) { toast.error("Billing rate required — enter a positive customer sell rate"); return; }
    if (rateDialog.job_material_id) {
      setOverrides((o) => ({ ...o, [rateDialog.lock_key]: val }));
      setEntries((rows) => rows.map((e) => e.lock_key === rateDialog.lock_key ? { ...e, rate: val, missing_rate: false } : e));
      setSelected((prev) => new Set([...prev, rateDialog.lock_key]));
      setRateDialog(null);
      return;
    }
    try {
      if (rateDialog.source_type === "labour") {
        await api.patch(`/users/${rateDialog.resource_id}/hourly-rate`, { charge_out_rate: val });
      } else if (rateDialog.source_type === "plant") {
        await api.patch(`/fleet/${rateDialog.resource_id}/charge-out-rate`, { charge_out_rate: val });
      } else {
        await api.patch(`/materials/${rateDialog.resource_id}`, { sell_price_per_unit: val });
      }
      toast.success(`Rate set — $${val}/${rateDialog.unit || "ea"}`);
      setRateDialog(null);
      load();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Couldn't set rate");
    }
  };

  const createInvoice = (targetInvoiceId = null) => materialReview.check(id, async (material_review_token, attention) => {
    const excluded = new Set((attention?.items || []).filter(item => item.billing_attention).map(item => item.lock_key));
    const included = new Set([...selected].filter(key => !excluded.has(key)));
    if (included.size === 0) { toast.error("No eligible items selected. Review materials or select other work to invoice."); return; }
    if (entries.some((e) => included.has(e.lock_key) && e.source_type === "materials" && (e.supplier_refs ? e.missing_rate : (!Number.isFinite(effRate(e)) || effRate(e) <= 0)))) { toast.error("Billing rate required for selected supplies"); return; }
    setCreating(true);
    try {
      const rate_overrides = {};
      included.forEach((k) => { if (overrides[k] != null) rate_overrides[k] = Number(overrides[k]); });
      const { data } = await api.post(`/jobs/${id}/charge-up/invoice`, {
        material_review_token, selected: [...included], from_date: fromDate || undefined, to_date: toDate || undefined, rate_overrides,
        target_invoice_id: targetInvoiceId || undefined,
      });
      if (data.dropped?.length) {
        toast.warning(`${data.dropped.length} item(s) were skipped (already claimed or missing a rate)`);
      }
      toast.success(data.appended ? `Added to invoice ${data.invoice.invoice_number}` : `Invoice ${data.invoice.invoice_number} created`);
      nav(`/app/customer-invoices/${data.invoice.id}`);
    } catch (e) {
      if (e.response?.data?.detail?.code === "material_review_required") throw e;
      toast.error(e.response?.data?.detail || "Couldn't create invoice");
    } finally {
      setCreating(false);
    }
  }, {selected: [...selected], rate_overrides: overrides});

  // If a draft already exists for this job, ask whether to add to it or start a new one.
  const onInvoiceClick = () => {
    if (selected.size === 0) { toast.error("Select at least one item to invoice."); return; }
    if (draftInvoices.length > 0) { setDraftChoice(true); return; }
    createInvoice(null);
  };

  if (loading && !job) {
    return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#FF5F15]" /></div>;
  }

  return (
    <div className="p-4 lg:p-6 pb-32 lg:pb-40 max-w-[1200px] mx-auto" data-testid="charge-up-workspace">
      <button onClick={() => nav(`/app/jobs/${id}`)} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-[#FF5F15] mb-3" data-testid="charge-up-back">
        <ArrowLeft className="w-3.5 h-3.5" /> {job?.title}
      </button>
      {materialReview.dialog}
      <MaterialBillingAttention jobId={id} details />
      {!loading && <TravelBillingPreview rows={labourPreview} />}

      <div className="hard-card p-5 mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="label-tag flex items-center gap-1.5"><Receipt className="w-3 h-3 text-[#FF5F15]" /> Invoice Now — Review Billing</div>
            <div className="font-display font-black uppercase tracking-tight text-2xl mt-1" data-testid="charge-up-workspace-total">
              {money(entries.filter((e) => e.billing_state === "unbilled").reduce((s, e) => s + effAmount(e), 0))}
            </div>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 mb-1">From</div>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} onBlur={load} className="h-9 rounded-md text-xs w-36" data-testid="charge-up-from-date" />
            </div>
            <div>
              <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 mb-1">To</div>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} onBlur={load} className="h-9 rounded-md text-xs w-36" data-testid="charge-up-to-date" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 w-40 rounded-md text-xs" data-testid="charge-up-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unbilled">Unbilled</SelectItem>
                <SelectItem value="in_draft">In Draft Invoice</SelectItem>
                <SelectItem value="billed">Billed</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {!loading && statusFilter === "unbilled" && draftMaterialCount > 0 && (
        <div className="hard-card p-4 mb-3 text-sm" data-testid="charge-up-existing-drafts">
          {draftMaterialCount} material item(s) already belong to a draft invoice. <button className="text-[#FF5F15] font-bold" onClick={() => setStatusFilter("in_draft")}>Review draft materials →</button>
        </div>
      )}
      {!loading && needsReview.length > 0 && <div className="hard-card p-4 mb-3" data-testid="charge-up-needs-review">
        <div className="label-tag mb-2">Materials &amp; Supplies — Details need review</div>
        {needsReview.map((doc) => <div key={doc.id} className="py-2 text-sm">
          <div className="font-bold">{doc.supplier_name || "Supplier document"} · {doc.total == null ? "Cost not recorded" : `${doc.currency} ${money(doc.total)}`}</div>
          <div className="text-xs text-zinc-500">Material details have not been confirmed. This document is not selectable for billing.</div>
          <button className="text-xs text-[#FF5F15] font-bold mt-1" onClick={() => nav(`/app/jobs/${id}/supplier-materials/${encodeURIComponent(doc.id)}/review`)}>Review supplier document →</button>
        </div>)}
      </div>}
      {!loading && missingLabourCount > 0 && canEditRates && (
        <div className="hard-card p-4 mb-3 border-l-4 border-[#FF5F15]" data-testid="charge-up-default-rate-banner">
          <div className="label-tag mb-1 flex items-center gap-1.5"><AlertTriangle className="w-3 h-3 text-[#FF5F15]" /> Billing rate required</div>
          <p className="text-sm font-bold mb-0.5">{missingLabourCount} labour {missingLabourCount === 1 ? "entry has" : "entries have"} no charge-out rate.</p>
          <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-3">
            Set one default charge-out rate and every worker without their own rate will be billed at it. You can give people different rates any time in the <button type="button" className="font-bold text-[#FF5F15] hover:underline" onClick={() => nav("/app/team")} data-testid="charge-up-default-rate-team-link">Team menu</button>.
          </p>
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 mb-1">Default charge-out rate ($/hr)</div>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-zinc-500">$</span>
                <Input type="number" step="0.01" min="0" value={defaultRateInput} onChange={(e) => setDefaultRateInput(e.target.value)} placeholder="e.g. 85" className="h-9 w-36 rounded-md pl-6 text-sm" data-testid="charge-up-default-rate-input" />
              </div>
            </div>
            <Button onClick={saveDefaultRate} disabled={savingDefaultRate} className="h-9 bg-[#FF5F15] hover:bg-[#E65100] text-white rounded-md" data-testid="charge-up-default-rate-save">
              {savingDefaultRate ? <Loader2 className="w-4 h-4 animate-spin" /> : "Set rate for all workers"}
            </Button>
          </div>
        </div>
      )}
      {loading ? (
        <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#FF5F15]" /></div>
      ) : entries.length === 0 ? (
        needsReview.length || (statusFilter === "unbilled" && draftMaterialCount) ? null : <div className="hard-card p-8 text-center text-sm text-zinc-500" data-testid="charge-up-empty">No {statusFilter !== "all" ? statusFilter.replace("_", " ") : ""} work found in this period.</div>
      ) : (
        <div className="space-y-3">
          {GROUPS.map(({ key, label, icon: Icon }) => {
            const rows = grouped[key];
            if (!rows.length) return null;
            const groupTotal = rows.reduce((s, e) => s + effAmount(e), 0);
            const isCollapsed = collapsed[key];
            return (
              <div key={key} className="hard-card overflow-hidden" data-testid={`charge-up-group-${key}`}>
                <button onClick={() => setCollapsed((c) => ({ ...c, [key]: !c[key] }))} className="w-full bg-zinc-100 dark:bg-white/[0.06] px-3.5 py-2.5 flex items-center justify-between">
                  <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest">
                    <Icon className="w-3.5 h-3.5 text-[#FF5F15]" /> {label} <span className="text-zinc-400 font-mono">({rows.length})</span>
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="font-mono font-bold text-sm">{money(groupTotal)}</span>
                    <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
                  </div>
                </button>
                {!isCollapsed && (
                  <div className="divide-y divide-zinc-100 dark:divide-white/5">
                    <div className="flex items-center gap-2 px-3.5 py-1.5 bg-zinc-50 dark:bg-white/[0.02]">
                      <button onClick={() => selectAll(key)} className="text-[9px] font-bold uppercase tracking-widest text-[#FF5F15]" data-testid={`charge-up-select-all-${key}`}>Select All</button>
                      <span className="text-zinc-300">·</span>
                      <button onClick={() => deselectAll(key)} className="text-[9px] font-bold uppercase tracking-widest text-zinc-400" data-testid={`charge-up-deselect-all-${key}`}>Deselect All</button>
                    </div>
                    {rows.map((e) => (
                      <div key={e.lock_key} className="flex items-center gap-3 px-3.5 py-2.5 flex-wrap" data-testid={`charge-up-entry-${e.lock_key}`}>
                        <input type="checkbox" checked={selected.has(e.lock_key)} disabled={e.billing_state !== "unbilled" || e.missing_rate || e.evidence_changed}
                          onChange={() => toggle(e.lock_key)} className="w-4 h-4 accent-[#FF5F15]" data-testid={`charge-up-check-${e.lock_key}`} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-bold truncate">{e.name}</div>
                          <div className="text-[11px] text-zinc-500 font-mono">{e.date || "Delivery date not recorded"} · {e.qty} {e.unit} {effRate(e) != null && `× $${effRate(e).toFixed(2)}`}</div>
                          {e.supplier_refs && <div className="text-[11px] text-zinc-500">{e.supplier_name} · {e.delivery_count} deliveries{e.docket_count > 0 ? ` · ${e.docket_count} dockets` : ""}</div>}
                          {(e.job_material_id || e.supplier_refs) && <div className="text-[11px] text-zinc-500">Supplier cost (internal): {e.supplier_cost == null ? "Not recorded" : money(e.supplier_cost)}
                            {e.job_material_id && e.billing_state === "unbilled" && <button onClick={() => nav(`/app/jobs/${id}/costs`)} className="ml-2 text-[#FF5F15]">Edit material details →</button>}
                          </div>}
                          {e.deliveries?.length > 0 && <details className="mt-2"><summary className="text-xs text-[#FF5F15] cursor-pointer">View deliveries / source details</summary><SupplierDeliveries deliveries={e.deliveries} currency={e.currency} /></details>}
                        </div>
                        {e.billing_state !== "unbilled" && (
                          <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded-md ${e.billing_state === "in_draft" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`} data-testid={`charge-up-state-${e.lock_key}`}>
                            {e.billing_state === "in_draft" ? `In Draft ${e.invoice_number || ""}` : `Billed ${e.invoice_number || ""}`}
                          </span>
                        )}
                        {e.invoice_id && e.invoice_id !== "external" && e.billing_state !== "unbilled" && <button className="text-xs text-[#FF5F15] font-bold" onClick={() => nav(`/app/customer-invoices/${e.invoice_id}`)}>Open invoice →</button>}
                        {e.supplier_refs && e.billing_state === "unbilled" && !e.evidence_changed ? <div className="w-full">
                          {e.missing_rate && <p className="text-xs text-amber-700">Customer rate required</p>}
                          <p className="text-xs">{e.mixed_rates ? "Individual source sell rates" : e.rate != null ? `Sell rate ${money(e.rate)} / ${e.unit}` : "Unpriced"} · Customer charge <strong>{money(e.amount)}</strong></p>
                          <SupplierPricingInline jobId={id} entry={e} onSaved={refreshPricing} />
                        </div> : e.evidence_changed ? <span className="text-xs text-red-600">Supplier evidence changed — review existing invoice</span> : e.missing_rate && e.billing_state === "unbilled" ? (
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-red-600 inline-flex items-center gap-1" data-testid={`charge-up-missing-${e.lock_key}`}>
                              <AlertTriangle className="w-3 h-3" /> Billing rate required
                            </span>
                            <Button size="sm" variant="outline" onClick={() => openSetRate(e)} className="h-7 rounded-md text-[10px] font-bold uppercase" data-testid={`charge-up-set-rate-${e.lock_key}`}>Set Rate</Button>
                          </div>
                        ) : e.billing_state === "unbilled" ? (
                          <div className="flex items-center gap-1.5">
                            {overrides[e.lock_key] != null && <span className="text-[8px] font-bold uppercase tracking-widest text-[#FF5F15]">OVERRIDDEN</span>}
                            <Input type="number" step="0.01" value={overrides[e.lock_key] ?? e.rate ?? ""}
                              onChange={(ev) => setOverrides((o) => ({ ...o, [e.lock_key]: ev.target.value }))}
                              className="h-7 w-20 rounded-md text-xs text-right" data-testid={`charge-up-rate-input-${e.lock_key}`} />
                            <span className="font-mono font-bold text-sm w-20 text-right" data-testid={`charge-up-amount-${e.lock_key}`}>{money(effAmount(e))}</span>
                          </div>
                        ) : (
                          <span className="font-mono font-bold text-sm w-20 text-right">{money(e.amount)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Invoice review sticky bar */}
      <div className="fixed bottom-0 left-0 lg:left-[220px] right-0 bg-white/97 dark:bg-[#0B0F14]/97 backdrop-blur border-t-2 border-zinc-300 dark:border-white/15 px-4 py-3" data-testid="charge-up-invoice-bar">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between flex-wrap gap-3">
          <div className="text-xs font-mono">
            <span className="text-zinc-400 uppercase text-[10px] font-sans font-bold tracking-wider mr-1.5">{selected.size} selected</span>
            Subtotal {money(subtotal)} · GST {money(gst)} ·
            <span className="text-base font-bold ml-1" data-testid="charge-up-grand-total">Total {money(grandTotal)}</span>
          </div>
          <Button onClick={onInvoiceClick} disabled={materialReview.busy || creating || selected.size === 0}
            className="h-10 rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-white font-bold uppercase tracking-wider text-xs px-6"
            data-testid="charge-up-create-invoice-btn">
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Invoice Selected
          </Button>
        </div>
      </div>

      <Dialog open={!!rateDialog} onOpenChange={(o) => !o && setRateDialog(null)}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="charge-up-set-rate-dialog">
          <DialogHeader>
            <DialogTitle>Set customer pricing</DialogTitle>
            <p className="text-sm break-words">{rateDialog?.name}</p>
            <DialogDescription>{rateDialog?.supplier_refs || rateDialog?.job_material_id ? "Enter the customer sell rate for this invoice." : "This sets the org default charge-out rate for future Charge-Up billing too."}</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm font-bold">$</span>
            <Input type="number" step="0.01" value={rateValue} onChange={(e) => setRateValue(e.target.value)} className="pl-7 rounded-sm" autoFocus data-testid="charge-up-rate-dialog-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRateDialog(null)} data-testid="charge-up-rate-dialog-cancel">Cancel</Button>
            <Button onClick={saveRate} className="bg-[#FF5F15] hover:bg-[#E65100] text-white" data-testid="charge-up-rate-dialog-save">Save Rate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={draftChoice} onOpenChange={(o) => !o && setDraftChoice(false)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="charge-up-draft-choice-dialog">
          <DialogHeader>
            <DialogTitle>This job already has a draft invoice</DialogTitle>
            <DialogDescription>Add the {selected.size} selected item{selected.size === 1 ? "" : "s"} to the existing draft, or start a brand-new invoice.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {draftInvoices.map(inv => <button key={inv.id} onClick={() => { setDraftChoice(false); createInvoice(inv.id); }}
              className="w-full flex items-center justify-between gap-3 rounded-md border border-[#FF5F15]/50 bg-[#FF5F15]/5 hover:bg-[#FF5F15]/10 px-4 py-3 text-left" data-testid={`charge-up-add-to-${inv.id}`}>
              <span className="min-w-0"><span className="font-bold block">Add to {inv.invoice_number}</span><span className="text-xs text-muted-foreground">{inv.title || "Draft invoice"} · {money(inv.total)}</span></span>
              <span className="text-[#FF5F15] font-bold shrink-0">Add →</span>
            </button>)}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDraftChoice(false)} data-testid="charge-up-draft-cancel">Cancel</Button>
            <Button onClick={() => { setDraftChoice(false); createInvoice(null); }} className="bg-zinc-800 hover:bg-zinc-700 text-white dark:bg-white/10" data-testid="charge-up-draft-new">Create a new invoice instead</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
